import { APIHelpers } from './apiHelpers';
import { ClaimStudioInstance, ScribeStudioInstance } from './models/studioInstance';

export const STUDIO_APPS_BACKEND_PROD = 'https://apps.api.manifoldxyz.dev';

export const studioAppsBackendBaseUrl =
  import.meta.env.DEV && import.meta.env.VITE_APP_STUDIO_API_URL // must be defined if true
    ? import.meta.env.VITE_APP_STUDIO_API_URL
    : `${STUDIO_APPS_BACKEND_PROD}`;

export enum CrossmintVerificationStatus {
  VERIFIED = 'verified',
  UNVERIFIED = 'unverified',
  UNDER_REVIEW = 'under_review',
  FAILED = 'failed',
}

export interface CrossmintVerificationResults {
  verificationStatus: {
    seller: {
      status: CrossmintVerificationStatus;
    };
    collection: {
      status: CrossmintVerificationStatus;
    };
  };
}

export interface IMerkleInfo {
  merkleProof: string[];
  value: number;
}

class AppsApi extends APIHelpers {
  constructor() {
    super();
    this.baseUrl = studioAppsBackendBaseUrl;
  }

  /**
   * Get a claim instance by id
   */
  async getClaimInstance(instanceId: number): Promise<ClaimStudioInstance> {
    return this.fetch(
      `/public/instance/data?id=${instanceId}&mediaPreview=true&maxMediaWidth=1024`,
    );
  }

  /**
   * Get a scribe instance by id
   */
  async getScribeInstance(instanceId: number): Promise<ScribeStudioInstance> {
    return this.fetch(
      `/public/instance/data?id=${instanceId}&mediaPreview=true&maxMediaWidth=1024`,
    );
  }

  /**
   * Get a merkle tree for a wallet
   */
  async getMerkleTree(merkleTreeId: number, walletAddress: string): Promise<IMerkleInfo[]> {
    return this.fetch(`/public/merkleTree/${merkleTreeId}/merkleInfo?address=${walletAddress}`);
  }

  /**
   * Post a transaction hash for an instance in order
   * to track the revenue for our tax purposes as a company.
   * Tracks the user transaction and their IP address.
   */
  async postTransaction(appId: number, instanceId: number, hash: string) {
    try {
      await this.post('/public/instance/tx', undefined, {
        appId: appId.toString(),
        instanceId: instanceId.toString(),
        hash,
      });
    } catch (error) {
      console.error('Error posting transaction', error);
    }
  }

  /**
   * Get verification status for a Crossmint instance
   */
  async getCrossmintVerificationStatus(crossmintId: string): Promise<CrossmintVerificationResults> {
    try {
      return this.fetch(`/public/instance/check-crossmint/${crossmintId}`);
    } catch {
      return {
        verificationStatus: {
          seller: {
            status: CrossmintVerificationStatus.FAILED,
          },
          collection: {
            status: CrossmintVerificationStatus.FAILED,
          },
        },
      };
    }
  }

  async getMintFeeTotal(
    networkId: number,
    contractAddress: string,
    instanceId: string,
    fromAddress?: string,
  ) {
    const query: Record<string, string> = {
      networkId: networkId.toString(),
      contractAddress,
      instanceId,
    };

    if (fromAddress) {
      query.fromAddress = fromAddress;
    }

    return this.fetch('/public/instance/mintFeeTotal', undefined, { ...query });
  }
}

export const appsApi = new AppsApi();
